import type { LoginResponse, MeResponse, ProblemDetails } from '@ifretes/shared'

/**
 * Cliente HTTP da API.
 *
 * O access token vive em MEMORIA — nunca em localStorage, que qualquer script
 * injetado consegue ler. A durabilidade da sessao vem do cookie httpOnly de
 * refresh, inacessivel a JavaScript.
 *
 * Consequencia: recarregar a pagina perde o access token. O bootstrap chama
 * /auth/refresh uma vez na abertura para reconstitui-lo a partir do cookie.
 */

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1'

let accessToken: string | null = null
/** Chamado quando a sessao morre de forma irrecuperavel. */
let aoPerderSessao: (() => void) | null = null

export function setAccessToken(t: string | null): void {
  accessToken = t
}
export function getAccessToken(): string | null {
  return accessToken
}
export function onSessaoPerdida(cb: () => void): void {
  aoPerderSessao = cb
}

/** Erro de API com o corpo RFC 7807 preservado, para a UI mostrar campo a campo. */
export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetails
  /** Extra do 403 de MFA obrigatorio (login de papel privilegiado). */
  readonly mfaEnrollmentRequired: boolean

  constructor(status: number, problem: ProblemDetails, extra?: Record<string, unknown>) {
    super(problem.detail ?? problem.title ?? `Erro ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
    this.mfaEnrollmentRequired = extra?.mfaEnrollmentRequired === true
  }

  /** Erros por campo, no formato que o formulario consome. */
  get camposComErro(): Record<string, string[]> {
    return this.problem.errors ?? {}
  }

  /** true quando a senha conferiu mas falta o codigo TOTP. */
  get precisaTotp(): boolean {
    return this.problem.type?.endsWith('/mfa-necessario') ?? false
  }
}

interface Opcoes {
  metodo?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  corpo?: unknown
  /** Interno: evita loop de refresh ao renovar o token. */
  semRetry?: boolean
  /**
   * Passe GPlanSiS: pede ao `/auth/refresh` que, sem sessao propria, abra uma
   * pelo cookie `gps_passe`. So na ENTRADA do sistema (ver store.tentarPasse);
   * o bootstrap da pagina publica nunca manda, para a vitrine nao virar painel.
   */
  passe?: boolean
}

/** Uma renovacao por vez: 3 respostas 401 simultaneas não devem virar 3 refreshes. */
let refreshEmVoo: Promise<boolean> | null = null

async function renovar(): Promise<boolean> {
  refreshEmVoo ??= (async () => {
    try {
      const r = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        // Ja dentro do app (so renova quem tinha access token): com a sessao
        // propria morta, o passe pode reabri-la, como antes.
        headers: { 'x-requested-with': 'ifretes-web', 'x-ifretes-passe': '1' },
      })
      if (!r.ok) return false
      const dados = (await r.json()) as LoginResponse
      accessToken = dados.accessToken
      return true
    } catch {
      return false
    } finally {
      refreshEmVoo = null
    }
  })()
  return refreshEmVoo
}

export async function requisitar<T>(caminho: string, opts: Opcoes = {}): Promise<T> {
  const headers: Record<string, string> = { 'x-requested-with': 'ifretes-web' }
  if (opts.passe) headers['x-ifretes-passe'] = '1'
  if (opts.corpo !== undefined) headers['content-type'] = 'application/json'
  if (accessToken) headers.authorization = `Bearer ${accessToken}`

  const resposta = await fetch(`${BASE}${caminho}`, {
    method: opts.metodo ?? 'GET',
    headers,
    // credentials sempre: o cookie de refresh precisa acompanhar as rotas de auth.
    credentials: 'include',
    body: opts.corpo !== undefined ? JSON.stringify(opts.corpo) : undefined,
  })

  // 401 com token expirado: renova uma vez e repete a chamada original. É o que
  // torna a expiração de 15 minutos invisível para quem está usando o sistema.
  if (resposta.status === 401 && !opts.semRetry && accessToken) {
    if (await renovar()) return requisitar<T>(caminho, { ...opts, semRetry: true })
    accessToken = null
    aoPerderSessao?.()
  }

  if (resposta.status === 204) return undefined as T

  const texto = await resposta.text()
  const corpo: unknown = texto ? JSON.parse(texto) : {}

  if (!resposta.ok) {
    const p = corpo as ProblemDetails & Record<string, unknown>
    throw new ApiError(
      resposta.status,
      {
        type: p.type ?? 'about:blank',
        title: p.title ?? 'Erro',
        status: resposta.status,
        detail: p.detail,
        errors: p.errors,
      },
      p,
    )
  }

  return corpo as T
}

// ─────────────────────────────────────────────────────────── endpoints

export const api = {
  /**
   * `emailEnviado` diz se o e-mail de confirmacao saiu DE FATO.
   *
   * A conta e criada de qualquer jeito (201). Sem este campo, a tela afirmava
   * "enviamos um link" mesmo quando o SMTP recusou, e a pessoa ficava
   * procurando na caixa de entrada uma mensagem que nunca ia chegar.
   */
  registrar: (corpo: unknown) =>
    requisitar<{ message: string; emailEnviado: boolean }>('/auth/register', {
      metodo: 'POST',
      corpo,
    }),

  confirmarEmail: (token: string) =>
    requisitar<{ message: string }>('/auth/verify-email', { metodo: 'POST', corpo: { token } }),

  entrar: (corpo: unknown) => requisitar<LoginResponse>('/auth/login', { metodo: 'POST', corpo }),

  sair: () => requisitar<{ message: string }>('/auth/logout', { metodo: 'POST' }),

  esqueciSenha: (email: string) =>
    requisitar<{ message: string }>('/auth/forgot-password', { metodo: 'POST', corpo: { email } }),

  redefinirSenha: (token: string, password: string) =>
    requisitar<{ message: string }>('/auth/reset-password', {
      metodo: 'POST',
      corpo: { token, password },
    }),

  mfaIniciar: () => requisitar<{ secret: string; otpauth: string }>('/auth/mfa/enroll', { metodo: 'POST' }),

  mfaConfirmar: (totp: string) =>
    requisitar<{ message: string; recoveryCodes: string[] }>('/auth/mfa/verify', {
      metodo: 'POST',
      corpo: { totp },
    }),

  eu: () => requisitar<MeResponse>('/me'),

  salvarPreferencias: (corpo: unknown) =>
    requisitar<Record<string, unknown>>('/me/preferences', { metodo: 'PATCH', corpo }),

  sessoes: () => requisitar<Array<Record<string, unknown>>>('/me/sessions'),

  encerrarSessao: (id: string) =>
    requisitar<{ message: string }>(`/me/sessions/${id}`, { metodo: 'DELETE' }),

  /** Reconstitui a sessao a partir do cookie httpOnly, na abertura do app. */
  bootstrap: () => requisitar<LoginResponse>('/auth/refresh', { metodo: 'POST', semRetry: true }),

  /** Mesmo `/auth/refresh`, mas aceitando o Passe GPlanSiS (so na entrada do sistema). */
  entrarPeloPasse: () =>
    requisitar<LoginResponse>('/auth/refresh', { metodo: 'POST', semRetry: true, passe: true }),
}
